import { convertValue, convertUnit, unitRound } from "./units";
import { swap } from "./utility";

let handleCounter = 0;

export default class Tag {
  constructor(session, parent, json) {
    // The prefix '$' is used to avoid name conflicts with properties defined by the application.
    Object.defineProperty(this, "$session", { value: session, configurable: true, enumerable: false });
    Object.defineProperty(this, "$parent", { value: parent, configurable: true, enumerable: false });
    Object.defineProperty(this, "$handleMap", { value: [], configurable: true, enumerable: false });

    this.committed = true;
    this.handle = json.handle || `temp-${handleCounter++}`;   // Assign a non-zero handle if zero or null.
    this.relevant = json.relevant ?? true;                    // Default true if omitted.
    this.settingOptional = json.settingOptional ?? false;     // Default false if omitted.
    this.settingEnabled = json.settingEnabled ?? true         // Default true if omitted.

    let handleMap = this.$root().$handleMap;
    handleMap[this.handle] = this;

    let jsonKeys = Object.keys(json);
    jsonKeys.forEach(key => {
      // Skip keys that have already been parsed above.
      let skipKeys = [ "handle", "settingEnabled", "settingOptional" ];
      if (skipKeys.includes(key))
        return;

      let value = json[key];
      if (hasHandle(value)) {
        this[key] = new Tag(session, this, value);
      }
      else if (key == "children") {
        this.children = value.map(childJson => {
          return new Tag(session, this, childJson);
        });
      }
      else {
        this[key] = value;
      }
    });
    
    // The '_children' property will be an alias of 'children' if this is an array tag.
    // Otherwise, it is a precomputed list of the object tag's children.
    // Precomputing '_children' prevents triggering re-evaluation of any computed()'s whose value depends on calls to .$children().
    // Previously, simple assignments like 'setpoint.value = 23' would trigger those computed()'s.
    Object.defineProperty(this, "_children", { configurable: true, value:
      this.children ??
      Object.values(this).filter(hasHandle)
    });
  }
  
  $root() {
    let tag = this;
    while (tag.$parent) {
      tag = tag.$parent;
    }
    return tag;
  }

  $key() {
    let parent = this.$parent;
    if (!parent) {
      return null;
    } 
    else if (parent.children) {
      return parent.children.indexOf(this);
    }
    else {
      return Object.keys(parent).find(key => parent[key] == this);
    }
  }

  $value() {
    if (this.value instanceof Tag)
      return this.value.$value();
    else
      return this.value;
  }

  $unit(unit) {
    if (this.value instanceof Tag)
      return this.value.$unit(unit);
    else
      return unit ?? this.unit;
  }

  $precision() {
    if (this.value instanceof Tag)
      return this.value.$precision();
    else
      return (1 / Math.pow(10, this.precision ?? 0));
  }

  $convertedValue(unit) {
    let value = this.$value();
    return convertValue(value, this.$unit(unit));
  }

  $convertedValueString(precision, unit) {
    if (this.$unit(unit))
      return unitRound(this.$convertedValue(unit), this.$convertedUnit(unit), precision ?? this.$precision());
    else
      return this.$value();
  }

  $convertedUnit(unit) {
    if (this.value instanceof Tag)
      return this.value.$convertedUnit();
    else if (unit ?? this.unit)
      return convertUnit(unit ?? this.unit);
    else
      return null;
  }

  $convertedMinPrecision() {
    return this.$precision();
  }

  $convertedMaxPrecision(convertedUnit) {
    const nativePrecision = this.$precision();
    convertedUnit = convertedUnit ?? this.$convertedUnit();
    if (convertedUnit?.includes("celsius") && nativePrecision == 0.1)
      return 0.05;
    else
      return nativePrecision;
  }

  $parents() {
    let tags = [];
    let parent = this.$parent;
    while (parent) {
      tags.push(parent);
      parent = parent.$parent;
    }
    tags.reverse();
    return tags;
  }

  $children() {
    // Directly returns .children if this is an array tag.
    // Otherwise, returns the pre-computed list of children in _children.
    return this.children ?? this._children;
  }
  
  $allChildren() {
    let allChildren = [];
    let children = this.$children();
    children.forEach(child => {
      allChildren.push(child);
      allChildren.push(... child.$allChildren());
    });
    return allChildren;
  }

  $fullName(filterFunc) {
    let tags = [ ... this.$parents(), this ].filter(tag => tag.name?.value);
    let filteredTags = filterFunc ? tags.filter(filterFunc) : tags; 

    // Omit sensor name if 1) this is sensor tag and 2) it is the aggregate's only sensor.
    let sensors = this.$parent?.$parent?.sensors?.children;
    if (sensors && sensors.length < 2)
      filteredTags.pop();
    
    return filteredTags.map(tag => tag.name.value).join(" - ");
  }

  $path() {
    let tags = [ ... this.$parents(), this ].slice(1);
    let keys = tags.map(tag => tag.$key());
    let path = keys.map(key => isNaN(key) ? `.${key}` : `[${key}]`).join("").slice(1);
    return path;
  }

  $atPath(path, forceArrayResult = false) {
    // Example usage:
    // 1) bin.$atPath("auxiliaries[].temperature") returns an array containing each auxiliary's temperature-tag.
    // 2) bin.$atPath("auxiliaries[0].temperature") returns the temperature-tag belonging to the first auxiliary.
    // 2) bin.$atPath("^.ipAddress") uses the '^' character to go "up" one level and then returns the system IP address.

    if (path == "")
      return forceArrayResult ? [ this ] : this;

    let tags = [ this ];
    let arrayResultType = forceArrayResult;
    path.match(/(\w+)|(\[\d*\])|\^/g).forEach(key => {
      if (key == "[]") {
        arrayResultType = true;
        tags = tags.flatMap(tag => tag.children);
      }
      else if (key[0] == "[") {
        let index = parseInt(key.substr(1));
        tags = tags.flatMap(tag => tag.children[index]).filter(child => child != null);
      }
      else if (key == "^") {
        tags = tags.map(tag => tag.$parent);
      }
      else {
        tags = tags.map(tag => tag[key]);
      }
      
      tags = tags.filter(tag => tag != null);
    });
    
    return (arrayResultType) ? tags : tags[0];
  }

  $clone() {
    let json = JSON.parse(JSON.stringify(this));
    
    // Change the handle of each cloned tag to prevent reuse of a handle by multiple tags.
    let arr = [ json ];
    for (let i = 0; i < arr.length; i++) {
      let json = arr[i];
      if (hasHandle(json)) {
        json.handle = `${json.handle}-clone${handleCounter++}`;
        let children = getChildren(json);
        children.forEach(child => arr.push(child));
      }
    }

    return new Tag(this.$session, this.$parent, json);
  }

  $update(json) {
    // Update the state of this tag from the given json.

    let handleMap = this.$root().$handleMap;
    this.relevant = json.relevant ?? true;                  // Default true if omitted.
    this.settingOptional = json.settingOptional ?? false;   // Default false if omitted.
    this.settingEnabled = json.settingEnabled ?? true       // Default true if omitted.
    
    let jsonKeys = Object.keys(json);
    jsonKeys.forEach(key => {
      let value = json[key];
      let skipKeys = [ "handle", "settingEnabled", "settingOptional" ];
      if (skipKeys.includes(key)) {
        return;
      }
      if (this[key] instanceof Tag) {
        this[key].$update(value);
      }
      else if (key == "children") {
        let sourceChildren = value;
        let areChildrenTags = this.children.some(child => child instanceof Tag) || sourceChildren.some(childJson => hasHandle(childJson));
        let shouldSortByHandle = areChildrenTags;
        if (shouldSortByHandle) {
          // Update from matches.
          let childrenToRemove = [];
          this.children.forEach(child => {
            let sourceChild = sourceChildren.find(sourceChild => sourceChild.handle == child.handle);
            if (sourceChild) child.$update(sourceChild);
            else childrenToRemove.push(child);
          });
          // Remove deleted entries.
          childrenToRemove.forEach(child => delete handleMap[child.handle]);
          this.children.splice(0, this.children.length, ... this.children.filter(child => !childrenToRemove.includes(child)));
          // Add new entries.
          let childrenToAdd_json = sourceChildren.filter(sourceChild => this.children.every(child => child.handle != sourceChild.handle));
          let childrenToAdd = childrenToAdd_json.map(json => new Tag(this.$session, this, json));
          this.children.push(... childrenToAdd);
          // Order according to 'sourceChildren'.
          sourceChildren.forEach((sourceChild, sourceIndex) => {
            let newIndex = sourceIndex;
            let oldIndex = this.children.findIndex(child => child.handle == sourceChild.handle);
            console.assert(oldIndex >= 0);
            swap(this.children, oldIndex, newIndex);
          });
        }
        else {
          for (let i = 0; i < sourceChildren.length; i++) {
            let childJson = sourceChildren[i];
            if (i  < this.children.length)
              updateObject(this.children[i], childJson);
            else
              this.children.push(childJson);
          }
          while (this.children.length > sourceChildren.length) {
            this.children.pop();
          }
        }
      }
      else if (key in this && typeof this[key] == "object" && this[key] != null && value != null) {
        updateObject(this[key], value);
      }
      else {
        this[key] = value;
      }
    });
  }
};

function hasHandle(json) {
  return (
    !Array.isArray(json) &&
    json instanceof Object &&
    "handle" in json
  );
}

function getChildren(json) {
  if (!hasHandle(json))
    return null;
  
  if (Array.isArray(json.children))
    return json.children;
  
  let children = [];
  let jsonKeys = Object.keys(json);
  jsonKeys.forEach(key => {
    let value = json[key];
    if (hasHandle(value))
      children.push(value);
  });
  return children;
}

function updateObject(target, source) {
  let isObject = (value) => (typeof value == "object" && value != null && !Array.isArray(value));
  let sourceKeys = Object.keys(source);
  sourceKeys.forEach(key => {
    let targetValue = target[key];
    let sourceValue = source[key];

    let areBothObjects = isObject(sourceValue) && isObject(targetValue);
    let areBothArrays = (Array.isArray(targetValue) && Array.isArray(sourceValue));
    if (areBothArrays) {
      for (let i = 0; i < sourceValue.length; i++) {
        let sourceElem = sourceValue[i];
        if (i < targetValue.length) {
          let targetElem = targetValue[i];
          let areBothElemsObjectish = (typeof targetElem == "object" && typeof sourceElem == "object" && targetElem != null && sourceElem != null);
          if (areBothElemsObjectish)
            updateObject(targetElem, sourceElem);
          else
            targetValue[i] = sourceElem;
        }
        else {
          targetValue[i] = sourceElem;
        }
      }
      while (targetValue.length > sourceValue.length)
        targetValue.pop();
    }
    else if (areBothObjects) {
      updateObject(targetValue, sourceValue);
    }
    else {
      target[key] = sourceValue;
    }
  });

  if (Array.isArray(target)) {
    console.assert(Array.isArray(source));
    while (target.length > source.length)
      target.pop();
  }
  else {
    console.assert(isObject(target));
    let targetKeys = Object.keys(target);
    targetKeys.forEach(key => {
      if (!(key in source))
        delete target[key];
    });
  }
}
